import React from 'react'
import { useTranslation } from 'react-i18next'
import Toast from 'react-toast'
import clipboard, { useClipboardQuery } from 'rich-clipboard'
import { ClipboardType } from '~/clipboard'
import { PlanSegue, Targeting } from '~/models'
import { TargetingSummaryLabel } from '~/ui/app/targeting'
import { memo } from '~/ui/component'
import { ConfirmBox, HBox, PopupMenu, PopupMenuItem, SVG, Tappable } from '~/ui/components'
import { createUseStyles, layout, shadows, ThemeProvider, useStyling } from '~/ui/styling'
import { useFlowPlanner } from '../FlowPlannerContext'

export interface Props {
  segue: PlanSegue
}

const TargetingAttribute = memo('TargetingAttribute', (props: Props) => {

  const {segue} = props

  const {planner}   = useFlowPlanner()

  const [clipboardQuery, clipboardItem] = useClipboardQuery<Targeting>(ClipboardType.TARGETING)

  const [t]      = useTranslation('flow_planner')
  const {colors} = useStyling()

  //------
  // Actions

  const edit = React.useCallback(() => {
    planner.editSegueDetail(segue, 'targeting')
  }, [planner, segue])

  const copy = React.useCallback(() => {
    clipboard.write([{
      type: ClipboardType.TARGETING,
      data: segue.targeting,
    }])

    Toast.show({
      ...t('segues.actions.copy_targeting.success'),
      type: 'success',
    })
  }, [segue, t])

  const paste = React.useCallback(() => {
    const targeting = clipboardQuery.getData()
    if (targeting == null) { return null }

    return planner.updateSegue(segue.uuid, segue => ({...segue, targeting}))
  }, [clipboardQuery, planner, segue.uuid])

  const remove = React.useCallback(async () => {
    const confirmed = await ConfirmBox.show({
      ...t('segues.actions.remove_targeting.confirm'),
      destructive: true,
    })
    if (!confirmed) { return }

    return await planner.updateSegue(segue.uuid, segue => ({...segue, targeting: Targeting.empty()}))
  }, [planner, segue.uuid, t])

  const menuItems = React.useMemo(() => {
    const items: PopupMenuItem[] = []

    items.push({
      icon:     'pencil',
      caption:  t('segues.actions.edit_targeting'),
      onSelect: edit,
    }, {
      icon:     'copy',
      caption:  t('segues.actions.copy_targeting.caption'),
      onSelect: copy,
    })

    if (clipboardItem != null) {
      items.push({
        icon:     'paste',
        caption:  t('segues.actions.paste_targeting'),
        onSelect: paste,
      })
    }

    items.push({
      section: '-',
    }, {
      icon:     'trash',
      caption:  t('segues.actions.remove_targeting.caption'),
      color:    colors.semantic.negative,
      onSelect: remove,
    })

    return items
  }, [clipboardItem, colors.semantic.negative, copy, edit, paste, remove, t])

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <PopupMenu items={menuItems}>
        {renderContent}
      </PopupMenu>
    )
  }

  function renderContent(toggle: () => any) {
    return (
      <Tappable onTap={toggle} onDoubleClick={edit}>
        <ThemeProvider contrast='secondary'>
          <HBox classNames={$.targetingAttribute} gap={layout.padding.inline.m}>
            <SVG name='target' size={layout.icon.s}/>
            <TargetingSummaryLabel
              targeting={segue.targeting}
              small
              caption
            />
          </HBox>
        </ThemeProvider>
      </Tappable>
    )
  }

  return render()

})

export default TargetingAttribute

export const height = 32

const useStyles = createUseStyles(theme => ({
  targetingAttribute: {
    height:       height,
    borderRadius: height / 2,
    padding:      [layout.padding.inline.s, layout.padding.inline.m],
    background:   theme.semantic.secondary,

    position: 'relative',
    '&:hover::after': {
      ...layout.overlay,
      content:       '""',
      pointerEvents: 'none',
      borderRadius:   height / 2,
      background:     theme.bg.hover,
      boxShadow:      shadows.depth(1),
    },
  },
}))